/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 21/12/21
 */
import React, {memo, useCallback, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Row, Col, Tag} from 'antd';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
// import {months} from '@/modules/audits/constants/months';
import {useGetOneAuditorForAudit} from '@/modules/users/hooks/useUsers';
import {useGetOneTemplateForAudit} from '@/modules/templates/hooks/audits/useTemplates';
import {useGetOneClientForAudit} from '@/modules/clients/hooks/useClients';
import {useGetOneEstablishmentForAudit} from '@/modules/clients/hooks/useClientsEstablishment';
import {useGetOneServiceForAudit} from '@/modules/settings/hooks/useServices';
import {useGetAreas} from '@/modules/settings/hooks/useNomenclaturesAreaType';
import map from 'lodash/map';
import {useClientFilter} from '@/hooks/useClientFilter';
import {useAuditWizard} from '@/modules/auditorAudits/hooks/useWizard';

const serviceIdHelper = (value) => {
  return (typeof value === 'object') ? value?._id : value;
};

const AuditorAuditsSummary = ({form}) => {
  const {t} = useTranslation('auditorAudits');
  const {currentService, valueGoal} = useClientFilter();
  const {setTemplateId, serviceId, setServiceId} = useAuditWizard();

  useEffect(() => {
    if (serviceId && serviceId !== currentService) {
      form.setFieldValue('template', null);
      setTemplateId(null);
      if (!currentService) {
        form.setFieldValue('service', null);
      }
    }
    setServiceId(currentService);
  }, [currentService, form, serviceId, setServiceId, setTemplateId]);

  const values = form?.getFieldsValue(true);
  const currentFormService = serviceIdHelper(values?.service);

  useEffect(() => {
    form.setFieldValue('valueGoal', valueGoal);
  }, [form, valueGoal]);

  const auditorParams = useMemo(() => ({useProjections: true, projections: ['_id', 'fullName']}), []);
  const templateParams = useMemo(() => ({useProjections: true, projections: ['_id', 'name', 'areaAccess']}), []);
  const clientParams = useMemo(() => ({useProjections: true, projections: ['_id', 'name']}), []);
  const establishmentParams = useMemo(() => ({useProjections: true, projections: ['_id', 'name']}), []);
  const serviceParams = useMemo(() => ({useProjections: true, projections: ['_id', 'name']}), []);
  const areaAccessParams = useMemo(() => ({useProjections: true, projections: {name: 1}}), []);

  const {data: auditor} = useGetOneAuditorForAudit(values?.auditor, auditorParams);
  const {data: template} = useGetOneTemplateForAudit(typeof values?.template === 'string' ? values?.template : values?.template?._id, templateParams);
  const {data: clientData} = useGetOneClientForAudit(values?.client, clientParams);
  const {data: establishmentData} = useGetOneEstablishmentForAudit(values?.establishment, establishmentParams);
  const {data: serviceData} = useGetOneServiceForAudit(currentService || currentFormService, serviceParams);
  const {data: areaAccessData} = useGetAreas(template?.areaAccess, areaAccessParams);

  const tagsRender = useCallback((value) => {
    return (<div className='flex flex-wrap'>
      {map(value, (item) => (
        <div key={`tag-${item['_id']}`} className="pb-2">
          <Tag className="px-2 py-1">
            {item['name']}
          </Tag>
        </div>
      ))}
    </div>);
  }, []);

  return (
    <div className="w-full">
      <Row gutter={16} className="my-4">
        <Col span={7}>
          <strong>{t('form.client')}:</strong>
        </Col>

        <Col span={17}>
          {clientData?.name}
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col span={7}>
          <strong>{t('form.establishment')}:</strong>
        </Col>

        <Col span={17}>
          {establishmentData?.name}
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col span={7}>
          <strong>{t('form.service')}:</strong>
        </Col>

        <Col span={17}>
          {serviceData?.name}
        </Col>
      </Row>


      <Row gutter={16} className="mb-4">
        <Col span={7}>
          <strong>{t('form.valueGoal')}:</strong>
        </Col>

        <Col span={17}>
          {valueGoal}%
        </Col>
      </Row>

      <Row gutter={16} className="my-4">
        <Col span={7}>
          <strong>{t('form.areaAccess')}:</strong>
        </Col>

        <Col span={17}>
          {areaAccessData?.length > 0 ? tagsRender(areaAccessData) : <span className="italic font-bold" style={{color: 'red'}}>{t('form.areaAccessMissing')}</span>}
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col span={7}>
          <strong>{t('form.yearAndMonth')}:</strong>
        </Col>

        <Col span={17}>
          {values?.startDate ? moment(values?.startDate)?.utc().format('MM/YYYY') : null}
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col span={7}>
          <strong>{t('form.date')}:</strong>
        </Col>

        <Col span={17}>
          {values?.date?.length ? `${moment(values?.date?.[0]).utc().format('ll')} / ${moment(values?.date?.[1]).utc().format('ll')}` : null}
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col span={7}>
          <strong>{t('form.auditor')}:</strong>
        </Col>

        <Col span={17}>
          {auditor?.fullName}
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col span={7}>
          <strong>{t('form.template')}:</strong>
        </Col>

        <Col span={17}>
          {template?.name}
        </Col>
      </Row>
    </div>
  );
};

AuditorAuditsSummary.propTypes = {
  form: PropTypes.any
};

export default memo(AuditorAuditsSummary);
